import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import moment from 'moment';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { getPromoterFinancials } from '../../../redux/actions/promoter';
import { hasEasypayPayoutAccount } from '../../../utils/easypayPayout';

/* ───────────────────────── shared atoms (local) ───────────────────────────── */
/* Same set every redesigned tab carries — see PaymentDetails, MyEvents. */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub, action }) => (
  <header className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
        {title}
      </h3>
      {sub && <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>}
    </div>
    {action}
  </header>
);

/* ───────────────────────────── formatting ─────────────────────────────────── */

const money = (value, locale, currency = 'EUR') =>
  new Intl.NumberFormat(locale === 'pt' ? 'pt-PT' : 'en-GB', {
    style: 'currency',
    currency,
  }).format(Number(value) || 0);

/** Share of the whole, as a whole number. 0 when there is no whole. */
const pctOf = (part, whole) => {
  const total = Number(whole) || 0;
  if (total <= 0) return 0;
  return Math.round((Number(part) / total) * 100);
};

/* ───────────────────────────── stat tiles ─────────────────────────────────── */

const TONES = {
  neutral: {
    value: 'text-ink-strong',
    bar: 'bg-ink-strong',
    icon: 'text-muted',
    chip: 'text-muted',
  },
  good: {
    value: 'text-good',
    bar: 'bg-good',
    icon: 'text-good',
    chip: 'text-good',
  },
  warn: {
    value: 'text-warn',
    bar: 'bg-warn',
    icon: 'text-warn',
    chip: 'text-warn',
  },
};

const StatCard = ({
  icon: Icon,
  label,
  value,
  caption,
  tone = 'neutral',
  percent,
}) => {
  const c = TONES[tone] || TONES.neutral;
  return (
    <Card className="flex flex-col justify-between">
      <div className="flex items-start justify-between gap-3">
        <Eyebrow>{label}</Eyebrow>
        {Icon && <Icon className={`w-[18px] h-[18px] shrink-0 ${c.icon}`} />}
      </div>
      <div
        className={`text-[26px] sm:text-[30px] font-bold tracking-tight leading-none ${c.value}`}
      >
        {value}
      </div>
      {typeof percent === 'number' && (
        <div className="mt-4 flex items-center gap-3">
          <div className="h-1.5 flex-1 rounded-full bg-paper-2 overflow-hidden">
            <div
              className={`h-full rounded-full ${c.bar}`}
              style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
            />
          </div>
          <span className={`text-[11.5px] font-bold shrink-0 ${c.chip}`}>
            {percent}%
          </span>
        </div>
      )}
      {caption && (
        <p className="text-[12px] text-muted mt-3 leading-relaxed">{caption}</p>
      )}
    </Card>
  );
};

/* ─────────────────────────────── the tab ──────────────────────────────────── */

/** How many event rows show before "Ver todos". */
const EVENT_ROWS_COLLAPSED = 5;

const Financials = ({ onSectionChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { financials, financialsLoading, financialsError, me } = useSelector(
    (state) => state.promoter
  );

  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    // The thunk rejects so callers can react; nothing here needs to, and an
    // unhandled rejection would surface as a console error on a tab the user
    // simply opened offline.
    dispatch(getPromoterFinancials()).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = financials?.currency || 'EUR';
  const fmt = useCallback(
    (value) => money(value, i18n.language, currency),
    [i18n.language, currency]
  );

  const totals = financials?.totals;
  const events = useMemo(() => financials?.events || [], [financials]);
  const upcoming = useMemo(
    () => financials?.upcomingRepasses || [],
    [financials]
  );
  const brands = useMemo(() => financials?.brands || [], [financials]);

  const easypayConnected = hasEasypayPayoutAccount(me);

  const visibleEvents = expanded
    ? events
    : events.slice(0, EVENT_ROWS_COLLAPSED);

  /* Export: built from what is already on screen, so the file and the page can
     never disagree. CSV rather than XLSX — it opens in everything and needs no
     dependency. */
  const handleExport = useCallback(() => {
    const header = [
      t('dashboard.financials_page.csv_event'),
      t('dashboard.financials_page.csv_start'),
      t('dashboard.financials_page.csv_bookings'),
      t('dashboard.financials_page.csv_brands'),
      t('dashboard.financials_page.csv_paid'),
      t('dashboard.financials_page.csv_repassed'),
      t('dashboard.financials_page.csv_pending'),
      t('dashboard.financials_page.csv_repass_date'),
      t('dashboard.financials_page.csv_gross'),
    ];

    const escape = (cell) => {
      const value = cell === null || cell === undefined ? '' : String(cell);
      return /[",;\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    };

    const rows = events.map((e) => [
      e.name,
      e.startDate || '',
      e.bookings,
      e.brands,
      e.paid,
      e.repassed,
      e.pending,
      e.repassAt ? moment(e.repassAt).format('YYYY-MM-DD') : '',
      e.grossPaidByBrands,
    ]);

    // Semicolons: Excel in pt-PT splits on ';', and a ',' file lands in one
    // column. The BOM is what makes it read the accents as UTF-8.
    const csv = [header, ...rows]
      .map((row) => row.map(escape).join(';'))
      .join('\n');

    const blob = new Blob([`﻿${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${t('dashboard.financials_page.export_filename')}-${moment().format(
      'YYYY-MM-DD'
    )}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [events, t]);

  /* ── loading / error / empty ─────────────────────────────────────────────── */

  if (financialsLoading && !financials) {
    return (
      <div className="min-h-[320px] flex items-center justify-center">
        <ClipLoader color="#FFBD59" size={32} />
      </div>
    );
  }

  if (financialsError && !financials) {
    return (
      <>
        <TabHeader
          eyebrow={t('dashboard.financials_page.eyebrow')}
          title={t('dashboard.financials_page.title')}
        />
        <Card className="text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-warn mx-auto mb-3" />
          <p className="text-[15px] font-bold text-ink-strong">
            {t('dashboard.financials_page.error_title')}
          </p>
          <p className="text-sm text-muted mt-1">{String(financialsError)}</p>
          <button
            type="button"
            onClick={load}
            className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-ink-strong text-white text-[13px] font-bold hover:bg-ink transition-colors"
          >
            {t('dashboard.financials_page.retry')}
          </button>
        </Card>
      </>
    );
  }

  // Before the first payment every figure is simply 0 — the layout stays the
  // same so a new promoter sees what the tab will hold, not a blank card.
  const hasData = (totals?.bookings || 0) > 0;
  const zero = fmt(0);

  return (
    <>
      <TabHeader
        eyebrow={t('dashboard.financials_page.eyebrow')}
        title={t('dashboard.financials_page.title')}
        sub={t('dashboard.financials_page.subtitle')}
        actions={
          hasData && (
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-ink-strong text-white text-[13px] font-bold hover:bg-ink transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {t('dashboard.financials_page.export')}
            </button>
          )
        }
      />

      <div className="space-y-5">
        {/* Connected accounts need financial facts, not programme messaging.
            Only show this panel when the promoter must take action. */}
        {!easypayConnected && (
          <div className="rounded-2xl border p-5 sm:p-6 bg-warn-soft/50 border-warn/30">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
                <BanknotesIcon className="w-[18px] h-[18px] text-yellow-03" />
              </div>
              <div className="min-w-0">
                <Eyebrow>
                  {t('dashboard.financials_page.easypay_eyebrow')}
                </Eyebrow>
                <p className="text-[13px] text-ink leading-relaxed">
                  {t('dashboard.financials_page.easypay_not_connected')}
                </p>
                <button
                  type="button"
                  onClick={() => onSectionChange?.('paymentDetails')}
                  className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-ink-strong hover:underline"
                >
                  {t('dashboard.financials_page.easypay_connect')}
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* The three headline numbers. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={BanknotesIcon}
            label={t('dashboard.financials_page.total_paid')}
            value={totals ? fmt(totals.paid) : zero}
            caption={t('dashboard.financials_page.events_brands', {
              events: totals?.events || 0,
              brands: totals?.brands || 0,
            })}
          />
          <StatCard
            icon={CheckCircleIcon}
            tone="good"
            label={t('dashboard.financials_page.repassed')}
            value={totals ? fmt(totals.repassed) : zero}
            percent={pctOf(totals?.repassed, totals?.paid)}
          />
          <StatCard
            icon={ClockIcon}
            tone="warn"
            label={t('dashboard.financials_page.pending')}
            value={totals ? fmt(totals.pending) : zero}
            percent={pctOf(totals?.pending, totals?.paid)}
          />
        </div>

        {/* How the promoter's share relates to what brands actually paid.
              Without this the headline reads as the gross and is ~5% off. */}
        <p className="text-[12px] text-muted leading-relaxed">
          {t('dashboard.financials_page.total_paid_hint')}
          {hasData &&
            ` ${t('dashboard.financials_page.gross_note', {
              gross: fmt(totals.grossPaidByBrands),
              fee: fmt(totals.there2bFee),
            })}`}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Per-event breakdown */}
          <Card className="lg:col-span-2" padded={false}>
            <div className="p-6 pb-0">
              <CardHeader
                eyebrow={t('dashboard.financials_page.by_event_eyebrow')}
                title={t('dashboard.financials_page.by_event_title')}
                action={
                  events.length > EVENT_ROWS_COLLAPSED && (
                    <button
                      type="button"
                      onClick={() => setExpanded((v) => !v)}
                      className="text-[12.5px] font-bold text-muted hover:text-ink-strong transition-colors shrink-0"
                    >
                      {expanded
                        ? t('dashboard.financials_page.show_less')
                        : t('dashboard.financials_page.show_all')}
                    </button>
                  )
                }
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-y border-line">
                    <th className="px-6 py-2.5 text-[10.5px] uppercase tracking-[0.12em] text-muted font-bold">
                      {t('dashboard.financials_page.col_event')}
                    </th>
                    <th className="px-3 py-2.5 text-[10.5px] uppercase tracking-[0.12em] text-muted font-bold text-right whitespace-nowrap">
                      {t('dashboard.financials_page.col_paid')}
                    </th>
                    <th className="px-3 py-2.5 text-[10.5px] uppercase tracking-[0.12em] text-muted font-bold text-right whitespace-nowrap">
                      {t('dashboard.financials_page.col_repassed')}
                    </th>
                    <th className="px-6 py-2.5 text-[10.5px] uppercase tracking-[0.12em] text-muted font-bold text-right whitespace-nowrap">
                      {t('dashboard.financials_page.col_pending')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-6 text-[13px] text-muted"
                      >
                        {t('dashboard.financials_page.empty_sub')}
                      </td>
                    </tr>
                  )}
                  {visibleEvents.map((e) => (
                    <tr
                      key={e.id}
                      className="border-b border-line/70 last:border-0"
                    >
                      {/* A long event name must not widen the table past
                            the card — the three money columns are the point of
                            the row, so the name is what gives. */}
                      <td className="px-6 py-3.5">
                        <div className="max-w-[150px] sm:max-w-[220px] lg:max-w-[260px]">
                          <div className="text-[13.5px] font-bold text-ink-strong truncate">
                            {e.name}
                          </div>
                          <div className="text-[11.5px] text-muted mt-0.5 truncate">
                            {t('dashboard.financials_page.brand_count', {
                              count: e.brands,
                            })}
                            {e.startDate
                              ? ` · ${moment(e.startDate).format('D MMM YYYY')}`
                              : ''}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5 text-right text-[13.5px] font-bold text-ink-strong whitespace-nowrap tabular-nums">
                        {fmt(e.paid)}
                      </td>
                      <td className="px-3 py-3.5 text-right text-[13.5px] font-bold whitespace-nowrap tabular-nums text-good">
                        {fmt(e.repassed)}
                      </td>
                      <td
                        className={`px-6 py-3.5 text-right text-[13.5px] font-bold whitespace-nowrap tabular-nums ${
                          e.pending > 0 ? 'text-warn' : 'text-muted'
                        }`}
                      >
                        {fmt(e.pending)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="space-y-5">
            {/* Upcoming transfers */}
            <Card>
              <CardHeader
                eyebrow={t('dashboard.financials_page.upcoming_eyebrow')}
                title={t('dashboard.financials_page.upcoming_title')}
              />
              {upcoming.length === 0 ? (
                <p className="text-[13px] text-muted">
                  {t('dashboard.financials_page.upcoming_empty')}
                </p>
              ) : (
                <>
                  <ul className="space-y-3.5">
                    {upcoming.slice(0, 5).map((u) => (
                      <li
                        key={u.id || `${u.eventId}:${u.repassAt}`}
                        className="flex items-start gap-3"
                      >
                        <div className="w-10 shrink-0 text-center">
                          {u.repassAt ? (
                            <>
                              <div className="text-[13px] font-bold text-ink-strong leading-none">
                                {moment(u.repassAt).format('DD')}
                              </div>
                              <div className="text-[9.5px] uppercase tracking-wider text-muted mt-1">
                                {moment(u.repassAt).format('MMM')}
                              </div>
                            </>
                          ) : (
                            <ClockIcon className="w-4 h-4 text-muted mx-auto" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-bold text-ink-strong truncate">
                            {u.eventName}
                          </div>
                          <div className="text-[11.5px] text-muted mt-0.5 truncate">
                            {!u.repassAt
                              ? t(
                                  'dashboard.financials_page.upcoming_date_unknown'
                                )
                              : t(
                                  'dashboard.financials_page.upcoming_scheduled'
                                )}
                          </div>
                        </div>
                        <div className="text-[13px] font-bold text-ink-strong whitespace-nowrap tabular-nums">
                          {fmt(u.amount)}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-muted mt-4 pt-4 border-t border-line leading-relaxed">
                    {t('dashboard.financials_page.upcoming_note')}
                  </p>
                </>
              )}
            </Card>

            {/* Who paid the most */}
            <Card>
              <CardHeader
                eyebrow={t('dashboard.financials_page.brands_eyebrow')}
                title={t('dashboard.financials_page.brands_title')}
              />
              {brands.length === 0 ? (
                <p className="text-[13px] text-muted">
                  {t('dashboard.financials_page.brands_empty')}
                </p>
              ) : (
                <ul className="space-y-3">
                  {brands.slice(0, 5).map((b) => (
                    <li key={b.id}>
                      <div className="flex items-baseline justify-between gap-3 mb-1.5">
                        <span className="text-[12.5px] font-bold text-ink-strong truncate">
                          {b.name || `#${b.id}`}
                        </span>
                        <span className="text-[12.5px] font-bold text-ink-strong whitespace-nowrap tabular-nums">
                          {fmt(b.paid)}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-paper-2 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-brand"
                          style={{
                            width: `${pctOf(b.paid, brands[0].paid)}%`,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Financials;
